import { useState, useCallback, type DragEvent, type ReactNode } from "react";
import { Image as ImageIcon } from "lucide-react";

interface BaseDropZoneProps {
  children: ReactNode;
  onDrop: (files: FileList) => void;
  disabled?: boolean;
  className?: string;
  /** Overlay copy while dragging. */
  dropLabel?: string;
}

export function BaseDropZone({ children, onDrop, disabled = false, className, dropLabel = "Drop image to attach" }: BaseDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && e.dataTransfer.types.includes("Files")) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Capture phase runs top-down before any nested drop target can
  // stopPropagation() in the bubble phase, so the overlay always clears —
  // even when a child (e.g. the lead-magnet PDF field) handles the drop
  // itself and stops the event from reaching handleDrop below.
  const handleDropCapture = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        onDrop(files);
      }
    },
    [disabled, onDrop]
  );

  return (
    <div
      className={className ?? "relative"}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDropCapture={handleDropCapture}
      onDrop={handleDrop}
    >
      {children}

      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-50 flex flex-col items-center justify-center gap-2 rounded-[inherit] border-2 border-primary-400 bg-primary-50/90">
          <span className="flex size-10 items-center justify-center rounded-xl bg-white text-primary-500 shadow-sm">
            <ImageIcon className="size-5" />
          </span>
          <p className="text-sm font-medium text-primary-600">{dropLabel}</p>
        </div>
      )}
    </div>
  );
}
